import { Modal, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CloudIcon from '../../../assets/images/upload.svg';
import warningIcon from '../../../assets/images/warning.svg';
import { transformFile } from '../../../components/DropZone';
import AlumlyButton from '../../../components/AlumlyButton';
import AlumlyDataGrid from '../../../components/AlumlyDataGrid';
import File from '../../../configs/file';
import { ServiceNameEnum } from '../../../enums/serviceName';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import DeleteFeaturedBannerModal from './DeleteFeaturedBannerModal';
import usePermission from '../../../hooks/usePermission';
import { PermissionEnum } from '../../../enums/permission';

const { Title, Text } = Typography;

export interface FeaturedBannerListT {
  id: number;
  title: string;
  message: string;
  sendTo: string;
  status: number;
  createdAt: string;
  createdBy: string;
}

export enum FileType {
  PNG = 'image/png',
  JPEG = 'image/jpeg',
}

export interface UploadResponse {
  id: number;
  presignUrl: string;
}

export interface Dimension {
  width: number;
  height: number;
}

export interface MyFile {
  key: string,
  id: number | null,
  type: string,
  name: string,
  size: number,
  preview: string,
  dimension: Dimension,
  file?: File,
  uploading?: boolean,
}

export interface UploadError {
  resolutionError: string;
  maxFileSizeError: string;
}

const FeaturedBannerList: React.FC = () => {
  const { maxUploadFileSize, allowDimension } = File.featuredBanner;
  const { service: uploadFileService } = useServices(`${ServiceNameEnum.file.upload}`);
  const { service: getFeaturedBannerService } = useServices(`${ServiceNameEnum.featuredBanner.index}`);
  const { service: deleteFeaturedBannerService } = useServices(`${ServiceNameEnum.featuredBanner.delete}`);
  const { service: uploadFeaturedBannerService } = useServices(`${ServiceNameEnum.featuredBanner.upload}`);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);
  const [featuredBannerLists, setFeaturedBannerList] = useState<any>([]);
  const [isRefreshData, setIsRefreshData] = useState<boolean>(false);
  const [isOpenDeleteBannerModal, setIsOpenDeleteBannerModal] = useState<boolean>(false);
  const [targetDeleteFeatureBannerId, setTargetDeleteFeatureBannerId] = useState<number | null>(null);
  const { checkPermission } = usePermission();

  const upload = (file: MyFile): Promise<UploadResponse> => new Promise((resolve, reject) => {
    const payload = new FormData();
    payload.append('file', file.file!);
    payload.append('sourceModel', 'course');

    uploadFileService.post(payload)
      .then(({ data, error }) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
  });

  const bytesToSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(`${Math.floor(Math.log(bytes) / Math.log(1024))}`, 10);
    return `${Math.round(bytes / 1024 ** i)} ${sizes[i]}`;
  };

  const getImageFeaturedBannerList = async (): Promise<void> => {
    setIsRefreshData(false);
    const { data, error } = await getFeaturedBannerService.get();
    if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
    } else {
      setFeaturedBannerList(data);
    }
  };

  const handleFileChange = async (event: any, displayOrder: Number): Promise<void> => {
    let file = event.target.files[0];
    [file] = (await transformFile([file]));

    if (file.size > maxUploadFileSize
       || file.dimension.width !== allowDimension.width || file.dimension.height !== allowDimension.height) {
      setUploadError({
        resolutionError: `Upload file resolution is wrong (${allowDimension.width}x${allowDimension.height})`,
        maxFileSizeError: ` Upload file size than ${bytesToSize(maxUploadFileSize)}`,
      });
      return;
    }
    upload(file).then(async (res) => {
      const uploadedFiles: any = {};
      uploadedFiles.id = res.id;
      uploadedFiles.preview = res.presignUrl;

      const postData = { fileId: res.id, displayOrder };
      const { error } = await uploadFeaturedBannerService.post(postData);
      if (error) {
        const errorResponse = AxiosService.getErrorResponse(error);
        toast.error(errorResponse?.message);
      } else {
        toast.success('Uploaded file successfully');
        setIsRefreshData(true);
      }
    });
  };

  const onClickClearImage = async (id: number): Promise<void> => {
    if (id) {
      const params = { id };
      const { error } = await deleteFeaturedBannerService.put({}, { params });
      if (error) {
        const errorResponse = AxiosService.getErrorResponse(error);
        toast.error(errorResponse?.message);
      } else {
        toast.success('Delete Image Success');
        setIsOpenDeleteBannerModal(false);
        setIsRefreshData(true);
      }
    } else {
      toast.error('Did Not Have Image Before');
    }
    setTargetDeleteFeatureBannerId(null);
  };

  const columns: any[] = [
    {
      field: 'id',
      headerName: 'Order',
      width: 120,
      renderCell: (params: any) => (
        params.row?.id && (
          <div style={{ marginTop: 40 }}>
            <Text strong>
              {params.row?.id}
            </Text>
          </div>
        )
      ),
    },
    {
      field: 'url',
      headerName: 'Image',
      width: 300,
      flex: 1,
      renderCell: (params: any) => (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {params.row?.url && params.row?.url !== '' ? (
            <div style={{ width: 130 }}>
              <img src={params.row?.url} alt="" style={{ height: 95, width: '100%', objectFit: 'cover' }} />
            </div>
          ) : (
            <div style={{ width: 130, height: 95, backgroundColor: '#efefef' }} />
          )}
          <div style={{ marginTop: 8 }}>
            <ul>
              <li>
                <Text type="secondary">Recommended resolution W 840 x H 1,152 px</Text>
              </li>
              <li>
                <Text type="secondary">maximum upload file size is 2 MB</Text>
              </li>
              <li>
                <Text type="secondary">Supported PNG, JPG, JPEG</Text>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      field: '',
      headerName: '',
      width: 280,
      renderCell: (params: any) => (
        <div style={{
          display: 'flex', flexDirection: 'row', gap: 8, marginTop: 24,
        }}
        >
          { checkPermission([PermissionEnum.code.productConfigEdit])
            && (
              <label htmlFor={`featured-banner-upload-${params.row?.id}`} style={{ display: 'inline-block' }}>
                <AlumlyButton icon={<img src={CloudIcon} alt="" />} width={130}>
                  Browse file
                </AlumlyButton>
                <input
                  id={`featured-banner-upload-${params.row?.id}`}
                  type="file"
                  accept="image/jpeg, image/png, image/jpg"
                  hidden
                  multiple={false}
                  onChange={(e) => handleFileChange(e, params.row?.id)}
                />
              </label>
            )}
          { checkPermission([PermissionEnum.code.productConfigDelete])
          && (
            <AlumlyButton
              label="rxvWhite"
              width={130}
              onClick={() => {
                setIsOpenDeleteBannerModal(true);
                setTargetDeleteFeatureBannerId(params.row?.featuredBannerId);
              }}
            >
              Clear image
            </AlumlyButton>
          )}
        </div>

      ),
    },
  ];

  const clearUploadError = (): void => {
    setUploadError(null);
  };

  useEffect(() => {
    getImageFeaturedBannerList();
  }, [isRefreshData]);

  return (
    <div style={{ marginTop: 32 }}>
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 32,
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex' }}>
            <Text style={{ marginRight: 8 }}>Total</Text>
            <Text strong>{`${featuredBannerLists.length} Items`}</Text>
          </div>
        </div>
      </div>

      <AlumlyDataGrid
        rowHeight={115}
        rows={featuredBannerLists || []}
        columns={columns}
        height={633}
        hideFooter
      />
      <Modal
        open={uploadError !== null}
        onCancel={clearUploadError}
        footer={null}
        centered
        width={460}
      >
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={warningIcon} alt="" style={{ width: 70, height: 70 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Title level={5}>{uploadError?.resolutionError ?? ''}</Title>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Title level={5}>{uploadError?.maxFileSizeError ?? ''}</Title>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 16,
              padding: '16px 0',
              backgroundColor: '#F1F5F9',
            }}
          >
            <AlumlyButton width={380} onClick={clearUploadError} label="rxv">
              Close
            </AlumlyButton>
          </div>
        </div>
      </Modal>
      <DeleteFeaturedBannerModal
        isOpen={isOpenDeleteBannerModal}
        handleClose={() => setIsOpenDeleteBannerModal(false)}
        featuredBannerId={targetDeleteFeatureBannerId}
        handleSubmit={(id) => onClickClearImage(id)}
      />
    </div>
  );
};

export default FeaturedBannerList;
